import { createLink, LinkComponent } from "@vixi/router";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/lib";
import { ComponentProps, ComponentPropsWithoutRef, forwardRef } from "react";

export const listItemLinkVariants = cva(
  "flex w-full max-w-full items-center text-[14px] overflow-hidden rounded-md gap-2.5 py-[9.5px] text-foreground text-start cursor-pointer focus-visible:bg-gray-300/70",
  {
    variants: {
      selected: {
        true: "bg-[#FEF0E6] text-primary hover:bg-[#FEF0E6]-hover",
        false: "bg-transparent text-foreground hover:bg-gray-100 focus-visible:bg-gray-300/70",
      },
      disabled: {
        true: "opacity-50 pointer-events-none",
        false: "",
      },
      mode: {
        compact: "min-w-auto justify-center px-2.75 h-10 w-10",
        expanded: "px-[11px_8px]",
      },
    },
    defaultVariants: {
      selected: false,
      disabled: false,
      mode: "expanded",
    },
  },
);

type StyledListItemLinkProps = ComponentPropsWithoutRef<"a"> &
  VariantProps<typeof listItemLinkVariants>;

const StyledListLink = forwardRef<HTMLAnchorElement, StyledListItemLinkProps>(
  ({ selected, className, disabled, mode, ...props }, ref) => {
    return (
      <a
        className={cn(listItemLinkVariants({ selected, disabled, mode }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);

const CreatedLinkComponent = createLink(StyledListLink);

export const ListItemLink: LinkComponent<typeof CreatedLinkComponent> = (props) => {
  return (
    <CreatedLinkComponent
      activeOptions={{
        explicitUndefined: false,
      }}
      activeProps={{
        selected: true,
      }}
      preload={"intent"}
      {...props}
    />
  );
};

export const ListItemText = forwardRef<
  HTMLSpanElement,
  ComponentProps<"span"> & Pick<VariantProps<typeof listItemLinkVariants>, "mode">
>(({ className, mode, ...props }, ref) => {
  return (
    <span
      className={cn(
        "text-[13px] leading-none font-medium text-inherit",
        mode === "compact" && "hidden",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
